// src/pages/CheckOptionsManager.js
// Admin/Supervisor managed dropdown lists for locations, activities, moods.
// Add, edit, reorder, and soft-delete options. Changes apply immediately to all staff.
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { getErrorMessage } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

const TYPE_LABELS = {
  location: 'Locations',
  activity: 'Activities / behaviors',
  mood: 'Mood / demeanor'
};

export default function CheckOptionsManager() {
  const { isSupervisor } = useAuth();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('location');
  const [newLabel, setNewLabel] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/check-options/all');
      setOptions(res.data);
    } catch (err) {
      console.error('Load options error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const addOption = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/check-options', { optionType: activeType, label: newLabel.trim() });
      setNewLabel('');
      flash(`"${newLabel.trim()}" added.`);
      load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    if (!editLabel.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api.patch(`/check-options/${id}`, { label: editLabel.trim() });
      setEditingId(null);
      flash('Option updated.');
      load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const removeOption = async (id, label) => {
    if (!window.confirm(`Remove "${label}" from the list?\n\nExisting check records that used this option will not be affected.`)) return;
    setSaving(true);
    setError('');
    try {
      const res = await api.delete(`/check-options/${id}`);
      flash(res.data.message);
      load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const restoreOption = async (id, label) => {
    setSaving(true);
    try {
      await api.patch(`/check-options/${id}`, { isActive: true });
      flash(`"${label}" restored.`);
      load();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const moveOption = async (id, direction, currentOrder, typeOptions) => {
    const idx = typeOptions.findIndex(o => o.id === id);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === typeOptions.length - 1) return;

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const newOrder = [...typeOptions];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];

    try {
      await api.post('/check-options/reorder', {
        optionType: activeType,
        orderedIds: newOrder.map(o => o.id)
      });
      load();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const typeOptions = options.filter(o => o.option_type === activeType);
  const activeOptions = typeOptions.filter(o => o.is_active);
  const inactiveOptions = typeOptions.filter(o => !o.is_active);

  return (
    <PageWrapper active="check-options" title="Check Options">
      <div>
        <div style={{ fontSize: 13, color: '#9CA3AF', marginBottom: 12 }}>
          Manage the dropdown lists staff see when logging a check. Changes apply immediately to all staff.
        </div>

        {successMsg && <div className="alert-bar success" style={{ marginBottom: 10 }}>{successMsg}</div>}
        {error && <div className="alert-bar danger" style={{ marginBottom: 10 }}>{error}</div>}

        {/* Type tabs */}
        <div className="tab-row">
          {Object.entries(TYPE_LABELS).map(([type, label]) => (
            <div
              key={type}
              className={`tab ${activeType === type ? 'active' : ''}`}
              onClick={() => { setActiveType(type); setEditingId(null); setError(''); }}
              style={{ fontSize: 12 }}
            >
              {label.split(' ')[0]}
            </div>
          ))}
        </div>

        <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>
          {TYPE_LABELS[activeType]} — {activeOptions.length} active
        </div>

        {/* Add new option */}
        <form onSubmit={addOption} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            placeholder={`Add new ${activeType}...`}
            style={{ flex: 1, padding: '9px 11px', border: '0.5px solid #E5E7EB', borderRadius: 7, fontSize: 14, color: '#1F2937', background: '#fff' }}
          />
          <button
            type="submit"
            disabled={saving || !newLabel.trim()}
            style={{ padding: '9px 16px', background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            Add
          </button>
        </form>

        {loading ? <div className="spinner" /> : (
          <>
            {activeOptions.length === 0 && (
              <div className="empty-state">No active {activeType} options. Add one above.</div>
            )}

            {/* Active options */}
            <div className="card" style={{ padding: '6px 0' }}>
              {activeOptions.map((opt, idx) => (
                <div key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: idx < activeOptions.length - 1 ? '0.5px solid #F3F4F6' : 'none' }}>
                  {/* Reorder buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <button
                      onClick={() => moveOption(opt.id, 'up', opt.display_order, activeOptions)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#9CA3AF', padding: '1px 3px', lineHeight: 1 }}
                      title="Move up"
                    >▲</button>
                    <button
                      onClick={() => moveOption(opt.id, 'down', opt.display_order, activeOptions)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#9CA3AF', padding: '1px 3px', lineHeight: 1 }}
                      title="Move down"
                    >▼</button>
                  </div>

                  {/* Label - editable */}
                  {editingId === opt.id ? (
                    <input
                      value={editLabel}
                      onChange={e => setEditLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(opt.id); if (e.key === 'Escape') setEditingId(null); }}
                      autoFocus
                      style={{ flex: 1, padding: '5px 8px', border: '1px solid #1D9E75', borderRadius: 5, fontSize: 13 }}
                    />
                  ) : (
                    <span style={{ flex: 1, fontSize: 13, color: '#1F2937' }}>{opt.label}</span>
                  )}

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 6 }}>
                    {editingId === opt.id ? (
                      <>
                        <button
                          onClick={() => saveEdit(opt.id)}
                          style={{ fontSize: 12, color: '#1D9E75', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                          disabled={saving}
                        >Save</button>
                        <button
                          onClick={() => setEditingId(null)}
                          style={{ fontSize: 12, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer' }}
                        >Cancel</button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { setEditingId(opt.id); setEditLabel(opt.label); }}
                          style={{ fontSize: 12, color: '#185FA5', background: 'none', border: 'none', cursor: 'pointer' }}
                        >Edit</button>
                        <button
                          onClick={() => removeOption(opt.id, opt.label)}
                          style={{ fontSize: 12, color: '#A32D2D', background: 'none', border: 'none', cursor: 'pointer' }}
                          disabled={saving}
                        >Remove</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Inactive / removed options */}
            {inactiveOptions.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '14px 0 8px' }}>
                  Removed ({inactiveOptions.length})
                </div>
                <div className="card" style={{ padding: '6px 0', opacity: 0.7 }}>
                  {inactiveOptions.map((opt, idx) => (
                    <div key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: idx < inactiveOptions.length - 1 ? '0.5px solid #F3F4F6' : 'none' }}>
                      <span style={{ flex: 1, fontSize: 13, color: '#9CA3AF', textDecoration: 'line-through' }}>{opt.label}</span>
                      <button
                        onClick={() => restoreOption(opt.id, opt.label)}
                        style={{ fontSize: 12, color: '#1D9E75', background: 'none', border: 'none', cursor: 'pointer' }}
                        disabled={saving}
                      >Restore</button>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>
                  Removed options no longer appear when logging checks but are preserved in all historical records.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
}
